import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import { updateUserProfile, executeQuery } from "../../../graphql/client";
import SignaturePad from "../../other/WaiverModal/SignaturePad";
import {
  WAIVER_TEXT,
  WAIVER_VERSION,
} from "../../other/WaiverModal/waiverContent";
// Reuse the Login page's chrome — .login-container / .login-box /
// .form-group / .error-message / .success-message / .login-btn /
// .auth-links / .switch-auth-btn / .password-requirements all live
// there. Once we have time, both pages should share a smaller
// auth-shell stylesheet.
import "../Login/style.css";
import { executePublicMutation } from "../../../graphql/client";

// Dedicated /signup page. Flow (order per David):
//   1. Email + password, then the Cognito email-verification code —
//      the account exists and is confirmed FIRST.
//   2. Auto sign-in (we still have the credentials in memory), which
//      lets steps 3-4 save through the authenticated updateUserProfile
//      mutation — no unauthenticated writes keyed by email.
//   3. T-shirt sizing question (men's / women's + size) — asked before
//      any other profile question. Women see explicit messaging that
//      their subscription is FREE while the guys pay. (The messaging
//      may be removed later; the question order stays.)
//   4. Liability / injury waiver signed by DRAWING their name on a
//      canvas. Signature PNG + typed name + waiver version go to the
//      socialUsers row; the server stamps the signed-at time.
//
// The backend `type=signup` call after Cognito accepts is unchanged —
// it links/creates the socialUsers row. Sex/shirt/waiver ride the
// authenticated mutation instead, where the answer-once sex lock and
// female auto-approve already live.
//
// Hardcoded Cognito config matches Login.js. If we ever rotate the
// pool / client, both files need the new values (or extract into one
// shared module — TODO).

const COGNITO_CONFIG = {
  userPoolId: "us-east-1_fc1omeTGh",
  clientId: "54fiucjkk95o6gna3l5l3dnjuo",
  region: "us-east-1",
};
const userPool = new CognitoUserPool({
  UserPoolId: COGNITO_CONFIG.userPoolId,
  ClientId: COGNITO_CONFIG.clientId,
});

const API_BASE_URL =
  (typeof process !== "undefined" &&
    process.env &&
    (process.env.REACT_APP_PENDING_JOIN_REQUESTS_API_URL ||
      process.env.REACT_APP_PJR_API_URL)) ||
  "https://ajje4ng1me.execute-api.us-east-1.amazonaws.com/prod/pending-join-requests";

const SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"];

export default function Signup() {
  const navigate = useNavigate();

  // Phase machine: "account" -> "verify" -> "shirt" -> "waiver" -> "done".
  const [phase, setPhase] = useState("account");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  // Profile step state (after verification).
  const [sex, setSex] = useState("");
  const [shirtSize, setShirtSize] = useState("M");
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  // ladiesFreeMessaging feature flag (admin Flags tab): when off, the
  // "ladies join free" banners stay hidden — women still get free
  // access behind the scenes, we just don't advertise it. Defaults to
  // shown if the flag fetch fails (matches the server default).
  const [showLadiesFree, setShowLadiesFree] = useState(true);
  useEffect(() => {
    let cancelled = false;
    executeQuery(`query { featureFlags { name value } }`)
      .then((res) => {
        if (cancelled) return;
        const flag = (res?.featureFlags || []).find(
          (f) => f.name === "ladiesFreeMessaging"
        );
        if (flag) setShowLadiesFree(!!flag.value);
      })
      .catch((err) => {
        console.warn("featureFlags fetch failed; using defaults", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Best-effort backend link call after Cognito accepts. Failures here
  // don't block the signup — the user can log in regardless; the link
  // just won't be set until an admin reconciles or the user signs up
  // again (which is a no-op on the Cognito side).
  const callBackendLink = async () => {
    try {
      // #134 phase 10: graphql signupUser (was REST type=signup).
      await executePublicMutation(
        `mutation SignupUser($email: String!, $password: String!, $name: String) {
          signupUser(email: $email, password: $password, name: $name) {
            success
            message
          }
        }`,
        {
          email: email.trim().toLowerCase(),
          password,
          // Never fall back to the email here — an email in the name
          // field leaks into member-visible rosters. Blank is fine;
          // the server also drops any name containing "@".
          name: name.includes("@") ? "" : name.trim(),
        }
      );
    } catch (err) {
      console.warn("Backend socialUsers link call failed", err);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (name.includes("@")) {
      setError(
        "That looks like an email address — please enter your name (like \"Jane Smith\") in the name field."
      );
      return;
    }
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 7) {
      setError("Please enter a valid phone number.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    // Lead capture: fire-and-forget name+email+phone (NEVER the
    // password) so an abandoned signup still leaves enough to follow
    // up with. Deliberately before / independent of the Cognito call —
    // even a password-policy rejection keeps the lead.
    // #134 phase 10: graphql captureSignupLead (was REST signupCapture).
    executePublicMutation(
      `mutation CaptureSignupLead($email: String!, $name: String, $phone: String) {
        captureSignupLead(email: $email, name: $name, phone: $phone)
      }`,
      {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        phone: phone.trim(),
      }
    ).catch(() => {});
    setLoading(true);
    const attributeList = [
      new CognitoUserAttribute({ Name: "email", Value: email.trim() }),
    ];
    if (name.trim()) {
      attributeList.push(
        new CognitoUserAttribute({ Name: "name", Value: name.trim() })
      );
    }
    userPool.signUp(
      email.trim(),
      password,
      attributeList,
      null,
      (err, result) => {
        setLoading(false);
        if (err) {
          // Half-finished signup: the account exists but was never
          // verified. Resend the code and jump to the verify step —
          // if the resend fails the account is already confirmed, so
          // point at sign-in / forgot-password instead of the
          // dead-end "already exists" error.
          if (err.code === "UsernameExistsException") {
            const existingUser = new CognitoUser({
              Username: email.trim(),
              Pool: userPool,
            });
            existingUser.resendConfirmationCode((resendErr) => {
              if (resendErr) {
                setError(
                  "An account with this email already exists. Try signing in — or use \"Forgot password?\" on the sign-in page if you don't remember your password."
                );
                return;
              }
              setPhase("verify");
              setInfo(
                "This email already started a signup but was never verified. We've emailed you a fresh code — enter it below. (After verifying, sign in with the password you created the first time.)"
              );
            });
            return;
          }
          setError(err.message || "Sign up failed.");
          return;
        }
        // Fire the backend link in the background — don't block the
        // verification step on it.
        callBackendLink();
        setPhase("verify");
        setInfo(
          "We've emailed you a verification code. Enter it below to finish signing up."
        );
      }
    );
  };

  // Sign in quietly with the credentials still in memory so the
  // t-shirt + waiver steps can save through the AUTHENTICATED profile
  // mutation. Only sessionStorage's token is set (what the graphql
  // client reads) — the full app session still starts at /login.
  const autoLogin = () =>
    new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email.trim(),
        Pool: userPool,
      });
      const details = new AuthenticationDetails({
        Username: email.trim(),
        Password: password,
      });
      cognitoUser.authenticateUser(details, {
        onSuccess: (session) => {
          try {
            sessionStorage.setItem(
              "cognitoIdToken",
              session.getIdToken().getJwtToken()
            );
            const rt = session.getRefreshToken && session.getRefreshToken();
            if (rt) {
              sessionStorage.setItem("cognitoRefreshToken", rt.getToken());
            }
          } catch (storageErr) {
            console.warn("Could not store session token", storageErr);
          }
          resolve(session);
        },
        onFailure: reject,
        newPasswordRequired: () =>
          reject(new Error("Password change required")),
      });
    });

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (!verificationCode.trim()) {
      setError("Verification code is required.");
      return;
    }
    setLoading(true);
    const cognitoUser = new CognitoUser({
      Username: email.trim(),
      Pool: userPool,
    });
    cognitoUser.confirmRegistration(verificationCode.trim(), true, async (err) => {
      if (err) {
        setLoading(false);
        setError(err.message || "Verification failed.");
        return;
      }
      // Verified — sign in behind the scenes and move on to the
      // t-shirt question. If the quiet sign-in fails for any reason,
      // fall back to the login page; the dashboard's profile banner
      // will collect the same answers later.
      try {
        await autoLogin();
        setLoading(false);
        setInfo("");
        setPhase("shirt");
      } catch (loginErr) {
        console.warn("Auto sign-in after verification failed", loginErr);
        setLoading(false);
        setInfo("Account verified. Redirecting to sign in…");
        // Hard load for the same stale-auth-state reason as the done
        // screen's dashboard button.
        setTimeout(() => {
          window.location.href = "/login";
        }, 1200);
      }
    });
  };

  const handleResendCode = () => {
    setError("");
    setInfo("");
    const cognitoUser = new CognitoUser({
      Username: email.trim(),
      Pool: userPool,
    });
    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        setError(err.message || "Failed to resend code.");
        return;
      }
      setInfo("Verification code resent.");
    });
  };

  // Save fit IMMEDIATELY on Continue — not at the end of the flow.
  // Lesson learned the hard way: sex used to persist only on the final
  // waiver submit, so a refresh between steps silently threw away the
  // answer (and a woman's auto-approval with it). Now the answer-once
  // sex lock + female auto-approve land the moment she taps Continue;
  // the waiver is a separate save and the dashboard's backfill modal
  // catches anyone who bails before signing.
  const handleShirtContinue = async () => {
    setError("");
    if (sex !== "male" && sex !== "female") {
      setError("Please choose men's or women's to continue.");
      return;
    }
    setLoading(true);
    try {
      // Phone rides along so the confirmed row has it authoritatively —
      // the Chats tab's GroupMe matching needs it, and saving it here
      // means new users never see the "add my phone number" step.
      await updateUserProfile({
        sex,
        shirtSize,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      // The name field seeds the waiver's legal-name input. Browser
      // autofill sometimes stuffs the EMAIL in there — don't seed the
      // waiver with something that isn't a name.
      if (name.includes("@")) setName("");
      setPhase("waiver");
    } catch (err) {
      setError(
        `Couldn't save (${err.message || err}). Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleWaiverSubmit = async () => {
    setError("");
    if (!name.trim() || name.includes("@")) {
      setError("Please enter your full name (not your email).");
      return;
    }
    if (!hasDrawn) {
      setError("Please sign with your finger or mouse to continue.");
      return;
    }
    let signatureDataUrl = "";
    try {
      signatureDataUrl = canvasRef.current.toDataURL("image/png");
    } catch (exportErr) {
      console.warn("Could not export signature:", exportErr);
    }
    setLoading(true);
    try {
      // Fit already saved at the shirt step; this save is waiver-only
      // (sex would be ignored by the server's answer-once lock anyway).
      await updateUserProfile({
        waiverSignature: signatureDataUrl || null,
        waiverName: name.trim(),
        waiverVersion: WAIVER_VERSION,
      });
      setPhase("done");
    } catch (saveErr) {
      setError(
        `Couldn't save your info (${saveErr.message || saveErr}). Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  const heading =
    phase === "verify"
      ? "Verify your email"
      : phase === "shirt"
      ? "T-shirt sizing"
      : phase === "waiver"
      ? "One quick signature"
      : phase === "done"
      ? "You're all set!"
      : "Create account";

  const stepNumber =
    phase === "account"
      ? 1
      : phase === "verify"
      ? 2
      : phase === "shirt"
      ? 3
      : 4;

  const sexButtonStyle = (value) => ({
    flex: 1,
    padding: "16px 12px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 8,
    cursor: "pointer",
    border: sex === value ? "2px solid #1f6feb" : "2px solid #d0d6e0",
    background: sex === value ? "#e8f0fe" : "#fff",
    color: sex === value ? "#1a56b0" : "#3a4150",
  });

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>{heading}</h1>

        {phase !== "done" && (
          <div
            aria-label={`Step ${stepNumber} of 4`}
            style={{ display: "flex", gap: 6, marginBottom: 14 }}
          >
            {["Account", "Verify", "Fit", "Waiver"].map((label, i) => {
              const step = i + 1;
              const state =
                step < stepNumber
                  ? "done"
                  : step === stepNumber
                  ? "current"
                  : "todo";
              return (
                <div key={label} style={{ flex: 1, textAlign: "center" }}>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      background:
                        state === "todo" ? "#e3e6ea" : "#1f6feb",
                      opacity: state === "done" ? 0.45 : 1,
                      marginBottom: 4,
                    }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: state === "current" ? 700 : 500,
                      color:
                        state === "current"
                          ? "#1f6feb"
                          : state === "done"
                          ? "#6d7688"
                          : "#a5adbb",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {state === "done" ? `✓ ${label}` : label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {info && <div className="success-message">{info}</div>}

        {/* ---- Step 1: account (email/password) ---- */}
        {phase === "account" && (
          <form onSubmit={handleSignup}>
            <div className="form-group">
              <label htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="signup-name">Name</label>
              <input
                id="signup-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="First and last name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="signup-phone">Phone number</label>
              <input
                id="signup-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                placeholder="(904) 555-1234"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="signup-confirm">Confirm password</label>
              <input
                id="signup-confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
            <div className="password-requirements">
              <p>Password requirements:</p>
              <ul>
                <li>At least 8 characters</li>
                <li>Uppercase + lowercase letters</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? "Creating account…" : "Create account"}
            </button>
            <div className="auth-links">
              <Link to="/login" className="switch-auth-btn">
                Already have an account? Sign in
              </Link>
            </div>
          </form>
        )}

        {/* ---- Step 2: email verification ---- */}
        {phase === "verify" && (
          <form onSubmit={handleVerify}>
            <p>
              We sent a verification code to <strong>{email}</strong>. Enter
              it below to finish signing up.
            </p>
            <p
              style={{
                background: "#fff8e1",
                border: "1px solid #ffe2a8",
                color: "#7a5b00",
                padding: "8px 12px",
                borderRadius: "4px",
                fontSize: "13px",
                margin: "0 0 12px",
              }}
            >
              <strong>Don't see it?</strong> Check your spam / promotions
              folder. The email comes from{" "}
              <code>activebeachesjax@gmail.com</code>. If it's still missing
              after a minute, hit Resend code.
            </p>
            <div className="form-group">
              <label htmlFor="signup-code">Verification code</label>
              <input
                id="signup-code"
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? "Verifying…" : "Verify"}
            </button>
            <div className="auth-links">
              <button
                type="button"
                className="switch-auth-btn"
                onClick={handleResendCode}
                disabled={loading}
              >
                Resend code
              </button>
              {" · "}
              <Link to="/login" className="switch-auth-btn">
                Back to sign in
              </Link>
            </div>
          </form>
        )}

        {/* ---- Step 3: t-shirt sizing (men's/women's first, then size) ---- */}
        {phase === "shirt" && (
          <div>
            <p style={{ marginTop: 0 }}>
              Quick t-shirt sizing question — which fit are you?
            </p>
            <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
              <button
                type="button"
                style={sexButtonStyle("female")}
                onClick={() => setSex("female")}
              >
                Women's
              </button>
              <button
                type="button"
                style={sexButtonStyle("male")}
                onClick={() => setSex("male")}
              >
                Men's
              </button>
            </div>

            {/* Only revealed AFTER picking Women's — guys never see
                that the ladies ride free. (Also behind the
                ladiesFreeMessaging feature flag.) */}
            {showLadiesFree && sex === "female" && (
              <div
                style={{
                  background: "#eef7ee",
                  border: "1px solid #b9dcb5",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 14,
                  color: "#1f4f1d",
                  marginBottom: 14,
                  textAlign: "left",
                }}
              >
                🎉 <strong>Ladies join free.</strong> Your subscription is{" "}
                <strong>100% free</strong> — full access to every event,
                location, and chat, on us. Only the guys pay for membership.
                💃
              </div>
            )}

            <div className="form-group">
              <label htmlFor="signup-shirt-size">Shirt size</label>
              <select
                id="signup-shirt-size"
                value={shirtSize}
                onChange={(e) => setShirtSize(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: 6,
                  border: "1px solid #d0d6e0",
                  fontSize: 15,
                }}
              >
                {SHIRT_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="login-btn"
              onClick={handleShirtContinue}
              disabled={loading}
            >
              {loading ? "Saving…" : "Continue"}
            </button>
          </div>
        )}

        {/* ---- Step 4: waiver + hand-drawn signature ---- */}
        {phase === "waiver" && (
          <div>
            <div
              style={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid #d0d6e0",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 12.5,
                color: "#3a4150",
                whiteSpace: "pre-wrap",
                marginBottom: 12,
                textAlign: "left",
              }}
            >
              {WAIVER_TEXT}
            </div>

            <div className="form-group">
              <label htmlFor="waiver-name">Your full name</label>
              <input
                id="waiver-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="First and last name"
                required
              />
            </div>

            <SignaturePad canvasRef={canvasRef} onDrawnChange={setHasDrawn} />

            <p style={{ fontSize: 12, color: "#5b6371", margin: "10px 0" }}>
              By signing and continuing, you agree to the waiver above.
            </p>

            <button
              type="button"
              className="login-btn"
              onClick={handleWaiverSubmit}
              disabled={loading}
            >
              {loading ? "Saving…" : "I agree — finish signing up"}
            </button>
            <div className="auth-links">
              <button
                type="button"
                className="switch-auth-btn"
                onClick={() => setPhase("shirt")}
                disabled={loading}
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* ---- Done ---- */}
        {phase === "done" && (
          <div>
            {showLadiesFree && sex === "female" && (
              <div
                style={{
                  background: "#eef7ee",
                  border: "1px solid #b9dcb5",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginBottom: 12,
                  color: "#1f4f1d",
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                🎉 You're in — ladies are free! 💃
                <div
                  style={{
                    fontWeight: 400,
                    fontSize: 13,
                    marginTop: 4,
                    color: "#3a6c36",
                  }}
                >
                  Your membership is active and event locations are
                  unlocked.
                </div>
              </div>
            )}
            <p style={{ fontSize: 15 }}>Your account is ready.</p>
            {sex === "male" && (
              <p style={{ fontSize: 14, color: "#5b6371" }}>
                Head to the <strong>Events</strong> tab — your Subscribe
                button is waiting at the top whenever you're ready to unlock
                locations and chat.
              </p>
            )}
            <button
              type="button"
              className="login-btn"
              onClick={() => {
                // HARD navigation on purpose: the quiet sign-in during
                // signup stored the session token AFTER the app booted,
                // so an in-app navigate() lands on a blank page with
                // stale auth state. A full page load re-initializes
                // everything from storage — the user is already signed
                // in and goes straight to their dashboard.
                window.location.href = "/dashboard";
              }}
            >
              Take me to my dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
